import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { switchMap } from 'rxjs/internal/operators/switchMap';
import { filter, Subscription, tap } from 'rxjs';

import { ATTENDANCE_SOURCE, ATTENDANCE_STATUS } from '@shared/constants';
import { IAttendance } from '@shared/interfaces';

import { LoggedUserService } from '../../auth/services';
import { AttendanceService } from '../services/attendance.service';

import {
  ClockAction,
  ConfirmationPopUpComponent,
} from './confirmation-popup/confirmation-popup.component';

@Component({
  selector: 'app-attendance-card',
  templateUrl: './attendance-card.component.html',
  styleUrls: ['./attendance-card.component.scss'],
  animations: [
    trigger('fadeInOut', [
      state('void', style({ opacity: 0 })),
      transition(':enter, :leave', [animate(300)]),
    ]),
  ],
})
export class AttendanceCardComponent implements OnInit, OnDestroy {
  date: Date;
  time: string;
  location: 'home' | 'office' | null = 'home';
  isClockedIn = false;
  isClockedOut = false;
  isDataLoading = false;
  initialDataLoading = true;
  disableClockInButton = false;
  hasActiveClockInFound = false;
  timeDifferenceIsValid = false;
  clockInTime: Date | null = null;
  clockOutTime: Date | null = null;
  clockedInTime: Date | null = null;
  formattedClockInTime: string;
  hours_worked: '';
  timeDifference = '';
  elapsedTime = '00:00:00';

  userSubscription: Subscription;
  private checkClockInInterval: ReturnType<typeof setInterval>;
  private timerInterval: ReturnType<typeof setInterval>;

  preDefined_max_clockIn_count = 2;
  @Input() clockIn_Count = 0;

  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
    private attendanceService: AttendanceService,
    private loggedUserService: LoggedUserService
  ) {
    this.date = new Date();
  }

  ngOnInit(): void {
    this.updateTime();
    setInterval(() => {
      this.updateTime();
    }, 1000);

    this.getLatestAttendanceConfiguration().then(() => {
      this.checkUserClockInStatus();
      this.checkClockInCount();
      this.startTimer();
    });
  }

  ngOnDestroy(): void {
    if (this.checkClockInInterval) {
      clearInterval(this.checkClockInInterval);
    }

    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }
  startTimer() {
    if (this.formattedClockInTime) {
      const startTime = this.clockInTime.getTime();
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        const now = new Date().getTime();
        const elapsed = new Date(now - startTime);
        this.elapsedTime = `${this.padZero(
          elapsed.getUTCHours()
        )}h:${this.padZero(elapsed.getUTCMinutes())}m:${this.padZero(
          elapsed.getUTCSeconds()
        )}s`;
      }, 1000);
    }
  }

  async checkUserClockInStatus() {
    this.initialDataLoading = true;
    this.loggedUserService.dataStore.subscribe((user) => {
      if (user && user._id) {
        this.attendanceService.checkUserClockInStatus(user._id).subscribe(
          (response) => {
            const hasActiveClockIn = response.isActive;
            this.clockInTime = response.clockedInTime;
            this.hasActiveClockInFound = hasActiveClockIn;

            if (hasActiveClockIn) {
              if (response.clockedInTime) {
                this.formattedClockInTime = new Date(
                  response.clockedInTime
                ).toLocaleString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                });
                const startTime = new Date(response.clockedInTime).getTime();
                if (this.timerInterval) clearInterval(this.timerInterval); // Clear any existing timer

                this.timerInterval = setInterval(() => {
                  const now = new Date().getTime();
                  const elapsed = new Date(now - startTime);
                  this.elapsedTime = `${this.padZero(
                    elapsed.getUTCHours()
                  )}h:${this.padZero(elapsed.getUTCMinutes())}m:${this.padZero(
                    elapsed.getUTCSeconds()
                  )}s`;
                }, 1000);
              }
              this.isDataLoading = false;
              this.initialDataLoading = false;
            } else {
              console.error('No active clock-in found.');
              this.initialDataLoading = false;
            }
          },
          (error) => {
            console.error('Error checking clock-in status', error);
            this.initialDataLoading = false;
          }
        );
      } else {
        console.error('User is not logged in');
        this.initialDataLoading = false;
      }
    });
    this.initialDataLoading = true;
  }

  async getLatestAttendanceConfiguration() {
    this.attendanceService
      .getLatestAttendanceConfiguration()
      .subscribe((response) => {
        if (!response.data) {
          const clockin_count = 2; // default value
          this.preDefined_max_clockIn_count = clockin_count;
        } else {
          this.preDefined_max_clockIn_count =
            response.data.max_numberOf_clockIns;
        }
      });
  }

  async checkClockInCount() {
    this.loggedUserService.dataStore.subscribe((user) => {
      if (user && user._id) {
        this.attendanceService.getClockInCount(user._id).subscribe(
          (response) => {
            this.clockIn_Count = response.clockInCount;

            if (this.clockIn_Count >= this.preDefined_max_clockIn_count) {
              this.disableClockInButton = true;
            } else {
              this.disableClockInButton = false;
            }
          },
          (error) => {
            console.error('Error checking clock-in count', error);
          }
        );
      } else {
        console.error('User is not logged in');
      }
    });
  }

  clockIn() {
    this.isDataLoading = true;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        location: this.location,
        action: ClockAction.CLOCK_IN,
        clockInCount: this.clockIn_Count,
        preDefined_max_clockIn_count: this.preDefined_max_clockIn_count,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter((result) => result === true),
        tap(() => {
          this.isClockedIn = true;
          this.clockInTime = new Date();
          this.startTimer();
        }),
        switchMap(() => {
          const postData: IAttendance = {
            clockedIn_time: this.clockInTime,
            clockedOut_time: null,
            is_clockedIn: true,
            place: this.location === 'home' ? 'Home' : 'Office',
            hours_worked: null,
            status: ATTENDANCE_STATUS.UNVERIFIED,
            source: ATTENDANCE_SOURCE.LAUNCHPAD,
            is_selected: false,
          };

          return this.attendanceService.clockIn(postData); // Return the observable properly
        })
      )
      .subscribe({
        next: () => {
          this.checkUserClockInStatus();
        },
        error: (error) => {
          console.error('Error during clock-in:', error);
        },
        complete: () => {
          this.isDataLoading = false;
        },
      });
  }

  clockOut() {
    this.isDataLoading = true;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        location: this.location,
        action: ClockAction.CLOCK_OUT,
        clockInCount: this.clockIn_Count,
        predefinedClockInCount: this.preDefined_max_clockIn_count,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.isClockedOut = true;
        this.clockOutTime = new Date();
        this.isDataLoading = false;
        this.stopTimer();
        this.calculateTimeDifference();

        const attendanceData: IAttendance = {
          clockedOut_time: this.clockOutTime,
          place: this.location === 'home' ? 'Home' : 'Office',
          clockedIn_time: this.clockInTime,
          is_clockedIn: false,
          hours_worked: this.timeDifference,
          status: ATTENDANCE_STATUS.UNVERIFIED,
          source: ATTENDANCE_SOURCE.LAUNCHPAD,
          is_selected: false,
        };

        // Call clockOut method from the service
        this.attendanceService.clockOut(attendanceData).subscribe(
          () => {
            return 0;
          },
          (error) => {
            throw new Error(
              'Error submitting clock-out data: ' + error.message
            );
          }
        );
      } else {
        this.isDataLoading = false;
      }
    });
  }

  // Function to update the time every second
  updateTime() {
    const now = new Date();
    this.time = now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  // Function to get the day (e.g., "Friday")
  getDay(): string {
    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    return days[this.date.getDay()];
  }

  // Function to get the date in "13th" format with superscript
  getDate(): string {
    const day = this.date.getDate();
    const suffix = this.getDaySuffix(day);
    return `${day}<sup>${suffix}</sup>`;
  }

  // Function to get month and year in "September 2024" format
  getMonthYear(): string {
    const month = this.date.toLocaleString('default', { month: 'long' });
    const year = this.date.getFullYear();
    return `${month} ${year}`;
  }

  // Function to determine the correct day suffix (e.g., "13th", "22nd")
  getDaySuffix(day: number): string {
    if (day > 3 && day < 21) return 'th'; // Special case for "teens"
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  // Function to select location
  selectLocation(location: 'home' | 'office') {
    this.location = location;
  }

  // Function to get clocked in time in a readable format
  getClockedInTime(): string {
    if (!this.clockInTime) return '';

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    return this.clockInTime.toLocaleString('en-US', options);
  }

  // Function to get clocked out time in a readable format
  getClockedOutTime(): string {
    if (!this.clockOutTime) return '';

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    return this.clockOutTime.toLocaleTimeString('en-US', options);
  }

  clockedInTimeConverter() {
    if (!this.clockedInTime) return '';
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };
    return this.clockedInTime.toLocaleTimeString('en-US', options);
  }

  padZero(num: number): string {
    return num.toString().padStart(2, '0');
  }

  // Function to stop the timer
  stopTimer() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  // Function to calculate the time difference between clock-in and clock-out
  calculateTimeDifference() {
    if (this.clockInTime && this.clockOutTime) {
      const clockInTime = new Date(this.clockInTime);
      const clockOutTime = new Date(this.clockOutTime);
      const diffMs = clockOutTime.getTime() - clockInTime.getTime();
      const diffHrs = Math.floor(diffMs / 3600000);
      const diffMins = Math.floor((diffMs % 3600000) / 60000);
      const diffSecs = Math.floor((diffMs % 60000) / 1000);

      // Format time difference as hh:mm:ss
      this.timeDifference = `${this.padZero(diffHrs)}h:${this.padZero(
        diffMins
      )}m:${this.padZero(diffSecs)}s`;

      // Check if the time difference is 8 hours or more (8 hours = 28800000 milliseconds)
      this.timeDifferenceIsValid = diffMs >= 28800000;
    }
  }

  // Method to dynamically set color based on time difference
  getTimeDifferenceClass(): string {
    return this.timeDifferenceIsValid ? 'time-valid' : 'time-invalid';
  }
}
